import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import pluralize from 'pluralize';
import lodashGroupBy from 'lodash/fp/groupBy';

import { SR_API_ACTION_GET_APARTMENT_BY_ID } from 'api/actionTypes';

import {
  DATA_TABLE_SOURCE_APARTMENTS,
  DATA_TABLE_VIEW_ID_APARTMENT_BY_ID,
} from 'common/dataTableConstants';

import { formatPrice } from 'common/formatters';

import { getListingSpecialDeals, getFloorPlanDisplayFields } from 'common/apartmentHelpers';

import {
  dataTable,
  actionDataTableLoadItems,
  actionInitializeDataTableState,
} from 'client/helpers/dataTables';

import { SCREEN_ID_LOCATOR_TOURS_APARTMENT_PAGE } from 'client/constants/screenIds';
import withTransitionHook from 'client/helpers/withTransitionHook';
import withScreenId from 'client/helpers/withScreenId';
import withApiState from 'client/helpers/withApiState';
import withScrollToTop from 'client/helpers/withScrollToTop';

import selectFloorPlansAction from 'client/actions/LocatorTours/selectFloorPlans';

import SrText from 'client/components/SrText';
import SrBox from 'client/components/SrBox';
import SrFlex from 'client/components/SrFlex';
import SrBackLink from 'client/components/SrBackLink';
import SrLocatorToursNavbar from 'client/components/SrLocatorToursNavbar';
import SrTag, { SR_TAG_THEME_GREEN_WHITE } from 'client/components/SrTag/SrTag';
import ButtonDeprecated, {
  BUTTON_THEME_YELLOW_GREYCLIFF,
  BUTTON_THEME_GREEN_INVERT_GREYCLIFF,
} from 'client/components/ButtonDeprecated';

function getFloorPlans(apartment, selectedFilters = {}) {
  if (typeof selectedFilters.bedrooms !== 'number') {
    return [];
  }
  const unitsByFloorPlanId = lodashGroupBy('floorPlanId', apartment.listings);
  let floorPlans = apartment.floorPlans
    .filter(({ tourableApartmentUnits }) => tourableApartmentUnits.length)
    .map((floorPlan) => getFloorPlanDisplayFields(floorPlan, unitsByFloorPlanId[floorPlan.id]));

  if (selectedFilters && typeof selectedFilters.bedrooms === 'number') {
    floorPlans = floorPlans.filter(({ bedrooms }) => {
      return selectedFilters.bedrooms === bedrooms;
    });
  }

  return floorPlans;
}

function getDataTableSettings({ apartmentId }) {
  const dataTableSettings = {
    dataSource: DATA_TABLE_SOURCE_APARTMENTS,
    viewId: [DATA_TABLE_VIEW_ID_APARTMENT_BY_ID, apartmentId].join(':'),
    count: 1,
    filter: {
      'apartments.id': parseInt(apartmentId, 10),
    },
    itemsPropName: 'apartments',
  };
  return dataTableSettings;
}

function getBedroomFilterOptionItems(selectedFilters) {
  return [
    {
      value: 0,
      label: 'Studio',
      isActive: selectedFilters.bedrooms === 0,
    },
    {
      value: 1,
      label: '1 Bed',
      isActive: selectedFilters.bedrooms === 1,
    },
    {
      value: 2,
      label: '2 Beds',
      isActive: selectedFilters.bedrooms === 2,
    },
    {
      value: 3,
      label: '3+ Beds',
      isActive: selectedFilters.bedrooms === 3,
    },
  ];
}

const SrApartmentRow = ({ apartmentSpecialDeals, unit }) => {
  const listingSpecialDeals = getListingSpecialDeals({
    listing: unit,
    apartmentSpecialDeals: apartmentSpecialDeals,
  });
  return (
    <SrBox className="SrApartmentRow" height="40px" padding="5px 0">
      <SrFlex justify="between" align="center" className="SrApartmentRow">
        <div>
          <SrFlex align="center">
            {unit.sunroomPhotoUrls[0] ? (
              <SrBox height="40px" paddingRight="10px">
                <img
                  width="40"
                  height="40"
                  alt={unit.sunroomPhotoUrls[0]}
                  src={unit.sunroomPhotoUrls[0]}
                  style={{ borderRadius: '7px' }}
                  className="dropshadow-bottom-edge"
                />
              </SrBox>
            ) : (
              <SrBox width="40px" height="40px" paddingRight="10px" />
            )}
            <SrText size="larger" weight="bolder" paddingRight="10px">
              {`#${unit.apartmentUnit}`}
            </SrText>
            {listingSpecialDeals.length > 0 && (
              <SrTag theme={SR_TAG_THEME_GREEN_WHITE}>{listingSpecialDeals[0].headline}</SrTag>
            )}
          </SrFlex>
        </div>
        <SrText color="green" size="larger" weight="medium" spacing="0">
          {formatPrice(unit.price)}
        </SrText>
      </SrFlex>
    </SrBox>
  );
};

SrApartmentRow.propTypes = {
  apartmentSpecialDeals: PropTypes.array,
  unit: PropTypes.object.isRequired,
};

SrApartmentRow.displayName = 'SrApartmentRow';

@withTransitionHook
@withScreenId(SCREEN_ID_LOCATOR_TOURS_APARTMENT_PAGE)
@withApiState({
  getApartmentByIdApiState: {
    action: SR_API_ACTION_GET_APARTMENT_BY_ID,
  },
})
@dataTable(({ match }) => {
  return getDataTableSettings({
    apartmentId: match.params.apartmentId,
  });
})
@withScrollToTop
class LocatorToursApartmentPage extends Component {
  static getPageMetadata = () => ({
    pageTitle: 'Locator Tours',
  });

  static fetchData(match, inject) {
    const dataTableSettings = getDataTableSettings({
      apartmentId: match.params.apartmentId,
    });
    actionInitializeDataTableState(inject, dataTableSettings);
    return actionDataTableLoadItems(inject, dataTableSettings);
  }

  static propTypes = {
    apartments: PropTypes.array.isRequired,
    getApartmentByIdApiState: PropTypes.object.isRequired,
    history: PropTypes.object.isRequired,
    match: PropTypes.object.isRequired,
    dispatch: PropTypes.func.isRequired,
  };

  static getDerivedStateFromProps(nextProps, prevState) {
    const [apartment] = nextProps.apartments;
    if (apartment && typeof prevState.selectedFilters.bedrooms !== 'number') {
      const unitsByFloorPlanId = lodashGroupBy('floorPlanId', apartment.listings);
      const floorPlans = apartment.floorPlans
        .filter(({ tourableApartmentUnits }) => tourableApartmentUnits.length)
        .map((floorPlan) => getFloorPlanDisplayFields(floorPlan, unitsByFloorPlanId[floorPlan.id]));
      const smallestBedCount = floorPlans.reduce((smallestBedCount, floorPlan) => {
        if (typeof floorPlan.bedrooms !== 'number') {
          return smallestBedCount;
        }
        if (!smallestBedCount || floorPlan.bedrooms < smallestBedCount) {
          return floorPlan.bedrooms;
        }
        return smallestBedCount;
      }, null);

      return {
        selectedFilters: {
          bedrooms: smallestBedCount,
        },
      };
    }
    return null;
  }

  constructor(props) {
    super(props);
    this.state = {
      selectedFloorPlans: [],
      selectedFilters: {
        bedrooms: null,
      },
    };
  }

  _onFloorPlanClick = (floorPlanId) => {
    const { selectedFloorPlans } = this.state;
    if (selectedFloorPlans.includes(floorPlanId)) {
      this.setState({
        selectedFloorPlans: selectedFloorPlans.filter((id) => floorPlanId !== id),
      });
    } else {
      this.setState({
        selectedFloorPlans: [...selectedFloorPlans, floorPlanId],
      });
    }
  };

  _onBedsFilterClick = (value) => {
    this.setState({
      selectedFilters: {
        ...this.state.selectedFilters,
        bedrooms: value,
      },
    });
  };

  _onSubmit = () => {
    const { history, match, dispatch } = this.props;
    const { selectedFloorPlans } = this.state;

    dispatch(selectFloorPlansAction, selectedFloorPlans).then(() => {
      history.push(`/locatortours/${match.params.apartmentId}/guest-card`);
    });
  };

  render() {
    const { apartments, getApartmentByIdApiState } = this.props;
    const { selectedFloorPlans, selectedFilters } = this.state;
    const [apartment] = apartments;

    if (getApartmentByIdApiState.inProgress || !apartment) {
      return <div className="loading-gif" />;
    }

    const bedroomOptionItems = getBedroomFilterOptionItems(selectedFilters);

    return (
      <div>
        <SrBox
          minHeight="225px"
          backgroundImage={`url(${apartment.photoUrls[0]})`}
          backgroundSize="cover"
          bgRepeat="no"
          paddingBottom={35}
        >
          <div>
            <SrLocatorToursNavbar withTopPadding noBackground>
              <SrBackLink to="/locatortours">Apartments</SrBackLink>
            </SrLocatorToursNavbar>
          </div>
          <SrBox margin="0 20px" textAlign="center" paddingTop="10px">
            <SrText
              color="white"
              size="40"
              weight="bolder"
              leading="45"
              align="center"
              font="greycliff"
              className="textShadowLarge"
              spacing="0"
            >
              {apartment.name}
            </SrText>
          </SrBox>
        </SrBox>

        <SrBox marginRight={10} marginLeft={10} marginBottom={200}>
          <SrBox backgroundColor="#EDEEEE" borderRadius="14px" height="44px" marginTop="10px">
            <SrFlex justify="around" height="100%" align="center">
              {bedroomOptionItems.map(({ label, value, isActive }) => (
                <SrBox
                  key={value}
                  backgroundColor={isActive ? '#FFFFFF' : 'transparent'}
                  borderRadius="9px"
                  padding="0 15px"
                  height="34px"
                  cursor="pointer"
                  onClick={() => this._onBedsFilterClick(value)}
                >
                  <SrText
                    className={(isActive && 'active') || null}
                    size="large"
                    weight="bolder"
                    leading="35"
                    whiteSpace="nowrap"
                    font="greycliff"
                  >
                    {label}
                  </SrText>
                </SrBox>
              ))}
            </SrFlex>
          </SrBox>
          <SrBox paddingTop="25px" paddingBottom="15px" paddingLeft="5px">
            <SrText size="xl" weight="bolder" font="greycliff">
              Choose floorplans to tour
            </SrText>
          </SrBox>

          {getFloorPlans(apartment, selectedFilters).map((floorPlan) => {
            const isSelected = selectedFloorPlans.includes(floorPlan.id);
            return (
              <SrBox
                key={floorPlan.id}
                backgroundColor={isSelected ? '#FFF7D7' : null}
                border={`2px solid ${isSelected ? '#FFCD00' : 'transparent'}`}
                borderRadius="14px"
                marginBottom="7px"
                boxShadow="0px 9px 18px rgba(25, 51, 64, 0.07)"
              >
                <div className={classnames(isSelected && 'active')}>
                  <SrBox
                    backgroundColor={isSelected ? '#FFEDA3' : '#FFF7D7'}
                    padding="14px 13px"
                    borderRadius="13px 13px 0 0"
                  >
                    <SrFlex justify="between">
                      <SrBox paddingRight={10}>
                        <SrText display="block" weight="bolder" size="larger" font="greycliff">
                          {floorPlan.displayName}
                        </SrText>
                        <SrBox paddingTop={2}>
                          <SrText
                            display="block"
                            weight="medium"
                            size="medium"
                            font="greycliff"
                            spacing="41"
                          >
                            {[
                              pluralize('Bed', floorPlan.bedrooms, true),
                              pluralize('Bath', floorPlan.bathrooms, true),
                              `${floorPlan.squareFeet} ft.`,
                            ].map((text) => (
                              <SrBox key={text} display="inlineBlock" paddingRight="10px">
                                {text}
                              </SrBox>
                            ))}
                          </SrText>
                        </SrBox>
                      </SrBox>
                      <div>
                        <SrBox width="148px">
                          <ButtonDeprecated
                            theme={
                              isSelected
                                ? BUTTON_THEME_GREEN_INVERT_GREYCLIFF
                                : BUTTON_THEME_YELLOW_GREYCLIFF
                            }
                            className={isSelected ? 'active' : null}
                            onClick={() => this._onFloorPlanClick(floorPlan.id)}
                            type="button"
                          >
                            {isSelected ? 'Chosen' : 'Choose'}
                          </ButtonDeprecated>
                        </SrBox>
                      </div>
                    </SrFlex>
                  </SrBox>
                  <SrBox padding="9px 13px">
                    <SrBox paddingBottom={9}>
                      <SrText weight="bolder" size="medium" font="greycliff">
                        <SrBox paddingBottom={5}>Show Unit</SrBox>
                        {floorPlan.showUnits.map((unit) => (
                          <SrApartmentRow
                            key={unit.id}
                            unit={unit}
                            apartmentSpecialDeals={apartment.apartmentSpecialDeals}
                          />
                        ))}
                      </SrText>
                    </SrBox>
                    {floorPlan.otherUnits.length > 0 && (
                      <SrBox paddingBottom={9}>
                        <SrText weight="bolder" size="medium" font="greycliff" color="grey600">
                          <SrBox paddingBottom={5}>Other Available Units</SrBox>
                          {floorPlan.otherUnits.map((unit) => (
                            <SrApartmentRow
                              key={unit.id}
                              unit={unit}
                              apartmentSpecialDeals={apartment.apartmentSpecialDeals}
                            />
                          ))}
                        </SrText>
                      </SrBox>
                    )}
                  </SrBox>
                </div>
              </SrBox>
            );
          })}
          {getFloorPlans(apartment, selectedFilters).length === 0 && (
            <SrBox paddingLeft="5px">
              <SrText font="greycliff">
                No {selectedFilters.bedrooms} beds available, please choose another tab.
              </SrText>
            </SrBox>
          )}
        </SrBox>
        <SrBox
          position="fixed"
          bottom="0"
          left="0"
          width="100%"
          boxShadow="0px -6px 17px rgba(219, 176, 0, 0.14)"
        >
          <SrBox
            width="100%"
            margin="0 auto"
            paddingBottom="43px"
            paddingTop="7px"
            className="greyBlur-90 border-box"
            paddingRight="10px"
            paddingLeft="10px"
          >
            <ButtonDeprecated
              theme={BUTTON_THEME_YELLOW_GREYCLIFF}
              disabled={selectedFloorPlans.length === 0}
              onClick={this._onSubmit}
              type="button"
            >
              Tour Selected Floorplans
            </ButtonDeprecated>
          </SrBox>
        </SrBox>
      </div>
    );
  }
}

LocatorToursApartmentPage.displayName = 'LocatorToursApartmentPage';

export default LocatorToursApartmentPage;
